import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Circle, CircleMarker, Popup, Tooltip } from 'react-leaflet';
import { Link } from 'react-router-dom';
import { Tool } from '../../types';
import 'leaflet/dist/leaflet.css';

interface MapViewProps {
  tools: Tool[];
  userLocation: { latitude: number; longitude: number } | null;
  radiusKm: number;
  buildToolLink?: (toolId: string) => string;
}

const calculateDistanceKm = (lat1: number, lng1: number, lat2: number, lng2: number) => {
  const toRad = (value: number) => (value * Math.PI) / 180;
  const earthRadiusKm = 6371;
  const deltaLat = toRad(lat2 - lat1);
  const deltaLng = toRad(lng2 - lng1);
  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
      Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadiusKm * c;
};

const getToolCoordinates = (tool: Tool) => {
  if (
    typeof tool.locationArea?.latitude === 'number' &&
    typeof tool.locationArea?.longitude === 'number'
  ) {
    return {
      latitude: tool.locationArea.latitude,
      longitude: tool.locationArea.longitude,
    };
  }

  if (typeof tool.latitude === 'number' && typeof tool.longitude === 'number') {
    return {
      latitude: tool.latitude,
      longitude: tool.longitude,
    };
  }

  return null;
};

const getZoomFromRadiusKm = (radiusKm: number) => {
  if (radiusKm <= 0.3) return 16;
  if (radiusKm <= 0.8) return 15;
  if (radiusKm <= 1.5) return 14;
  if (radiusKm <= 2) return 13;
  if (radiusKm <= 5) return 12;
  if (radiusKm <= 10) return 11;
  if (radiusKm <= 20) return 10;
  if (radiusKm <= 35) return 9;
  return 8;
};

const formatDistance = (distanceKm: number) => {
  if (distanceKm < 1) return `${Math.round(distanceKm * 1000)} m`;
  return `${distanceKm.toFixed(1)} km`;
};

const MapView: React.FC<MapViewProps> = ({ tools, userLocation, radiusKm, buildToolLink }) => {
  const [showToolList, setShowToolList] = useState(false);
  const [previewTool, setPreviewTool] = useState<{ tool: Tool; distanceKm: number | null } | null>(null);
  const getToolLink = (toolId: string) => (buildToolLink ? buildToolLink(toolId) : `/tool/${toolId}`);

  useEffect(() => {
    if (!userLocation) {
      setShowToolList(false);
      setPreviewTool(null);
    }
  }, [userLocation]);

  const toolsWithCoordinates = useMemo(() => {
    return tools
      .map((tool) => {
        const coords = getToolCoordinates(tool);
        if (!coords) return null;

        const distanceKm = userLocation
          ? calculateDistanceKm(
              userLocation.latitude,
              userLocation.longitude,
              coords.latitude,
              coords.longitude
            )
          : null;

        return {
          tool,
          latitude: coords.latitude,
          longitude: coords.longitude,
          distanceKm,
        };
      })
      .filter(Boolean) as Array<{
      tool: Tool;
      latitude: number;
      longitude: number;
      distanceKm: number | null;
    }>;
  }, [tools, userLocation]);

  const toolsInRadius = useMemo(() => {
    if (!userLocation) return [];
    return toolsWithCoordinates
      .filter((item) => (item.distanceKm ?? Infinity) <= radiusKm)
      .sort((a, b) => (a.distanceKm ?? Infinity) - (b.distanceKm ?? Infinity));
  }, [toolsWithCoordinates, userLocation, radiusKm]);

  const centerLat = userLocation?.latitude ?? 55.6761;
  const centerLng = userLocation?.longitude ?? 12.5683;
  const zoom = userLocation ? getZoomFromRadiusKm(radiusKm) : 11;

  return (
    <div className="relative h-[calc(100vh-300px)] min-h-[500px] w-full rounded-lg overflow-hidden border border-gray-200 shadow-sm">
      <MapContainer
        center={[centerLat, centerLng]}
        zoom={zoom}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {userLocation && (
          <>
            <Circle
              center={[userLocation.latitude, userLocation.longitude]}
              radius={radiusKm * 1000}
              eventHandlers={{
                click: () => setShowToolList(true),
              }}
              pathOptions={{
                color: '#15803d',
                fillColor: '#22c55e',
                fillOpacity: 0.16,
                weight: 3,
              }}
            >
              <Tooltip direction="center" permanent opacity={1}>
                <div className="text-center">
                  <div className="font-semibold text-gray-900 text-sm">{toolsInRadius.length} værktøj</div>
                  <div className="text-[11px] text-gray-600">Klik for liste</div>
                </div>
              </Tooltip>
            </Circle>

            <CircleMarker
              center={[userLocation.latitude, userLocation.longitude]}
              radius={7}
              pathOptions={{
                color: '#065f46',
                fillColor: '#10b981',
                fillOpacity: 1,
                weight: 2,
              }}
            >
              <Tooltip direction="top" offset={[0, -8]} opacity={1}>
                Din position
              </Tooltip>
            </CircleMarker>
          </>
        )}

        {!userLocation &&
          toolsWithCoordinates.map(({ tool, latitude, longitude }) => {
            const pricePerDay = tool.pricePerDayCents / 100;

            return (
              <CircleMarker
                key={`fallback-${tool.id}`}
                center={[latitude, longitude]}
                radius={6}
                pathOptions={{
                  color: '#166534',
                  fillColor: '#22c55e',
                  fillOpacity: 0.9,
                  weight: 2,
                }}
              >
                <Popup>
                <div className="p-2 min-w-[200px]">
                  <Link to={getToolLink(tool.id)} className="block">
                    <img
                      src={tool.primaryImageUrl}
                      alt={tool.name}
                      className="w-full h-32 object-cover rounded-md mb-2"
                    />
                    <h3 className="font-medium text-gray-900 mb-1">{tool.name}</h3>
                    {(tool.city || tool.location) && (
                      <p className="text-sm text-gray-600 mb-2">{tool.city || tool.location}</p>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-green-600">
                        {pricePerDay} kr./dag
                      </span>
                      <div className="flex items-center">
                        <span className="text-yellow-500 text-sm mr-1">★</span>
                        <span className="text-sm text-gray-600">{tool.rating || tool.ratingAverage || 0}</span>
                      </div>
                    </div>
                  </Link>
                </div>
                </Popup>
              </CircleMarker>
            );
          })}
      </MapContainer>

      {userLocation && showToolList && (
        <div className="absolute right-3 top-3 z-[900] w-[calc(100%-1.5rem)] max-w-sm rounded-lg border border-gray-200 bg-white/95 p-3 shadow-lg backdrop-blur-sm">
          <div className="mb-2 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Værktøj i radius</h3>
              <p className="text-xs text-gray-500">Sorterede efter nærmeste</p>
            </div>
            <button
              onClick={() => setShowToolList(false)}
              className="text-xs font-medium text-gray-600 hover:text-gray-900"
            >
              Luk
            </button>
          </div>

          {toolsInRadius.length === 0 ? (
            <p className="text-sm text-gray-600">Intet værktøj i denne afstand.</p>
          ) : (
            <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
              {toolsInRadius.map(({ tool, distanceKm }) => {
                const pricePerDay = (tool.pricePerDayCents || 0) / 100;

                return (
                  <button
                    key={`radius-list-${tool.id}`}
                    type="button"
                    onClick={() => setPreviewTool({ tool, distanceKm })}
                    className="flex w-full items-center justify-between rounded-md border border-gray-100 bg-white px-3 py-2 text-left hover:border-green-200 hover:bg-green-50"
                  >
                    <div className="flex min-w-0 items-center pr-3">
                      <img
                        src={tool.primaryImageUrl}
                        alt={tool.name}
                        className="mr-3 h-10 w-10 rounded-md object-cover"
                      />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-gray-900">{tool.name}</p>
                        {(tool.city || tool.location) && (
                          <p className="truncate text-xs text-gray-500">{tool.city || tool.location}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-medium text-green-700">{formatDistance(distanceKm ?? 0)}</p>
                      <p className="text-xs text-gray-500">{pricePerDay} kr./dag</p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {previewTool && (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4">
          <button
            type="button"
            onClick={() => setPreviewTool(null)}
            className="absolute inset-0 bg-black/45"
            aria-label="Luk forhåndsvisning"
          />
          <div className="relative z-10 w-full max-w-md overflow-hidden rounded-xl bg-white shadow-2xl">
            <img
              src={previewTool.tool.primaryImageUrl}
              alt={previewTool.tool.name}
              className="h-52 w-full object-cover"
            />
            <div className="p-4">
              <h3 className="text-lg font-semibold text-gray-900">{previewTool.tool.name}</h3>
              {(previewTool.tool.city || previewTool.tool.location) && (
                <p className="mt-1 text-sm text-gray-600">{previewTool.tool.city || previewTool.tool.location}</p>
              )}
              <div className="mt-3 flex items-center justify-between">
                <p className="text-sm font-medium text-green-700">
                  {formatDistance(previewTool.distanceKm ?? 0)} fra dig
                </p>
                <p className="text-sm text-gray-700">{(previewTool.tool.pricePerDayCents || 0) / 100} kr./dag</p>
              </div>
              <div className="mt-4 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setPreviewTool(null)}
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Luk
                </button>
                <Link
                  to={getToolLink(previewTool.tool.id)}
                  onClick={() => setPreviewTool(null)}
                  className="rounded-md bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700"
                >
                  Gå til værktøj
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapView;
